"use client";

import * as React from "react";

import { type Editor, isNodeSelection } from "@tiptap/react";

// --- Icons ---
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faHeading,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
// --- UI Primitives ---
import {
  UnifiedButton,
  UnifiedButtonProps,
} from "@/components/tiptap-ui/unified-button";
// --- Tiptap UI ---
import {
  type Level,
  getFormattedHeadingName,
  headingIcons,
} from "@/components/tiptap-ui/heading-button/heading-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { isNodeInSchema } from "@/lib/tiptap-utils";

interface HeadingDropdownMenuProps extends Omit<UnifiedButtonProps, "type"> {
  editor?: Editor | null;
  levels?: Level[];
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function HeadingDropdownMenu({
  editor: providedEditor,
  levels = [1, 2, 3, 4, 5, 6],
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: HeadingDropdownMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const editor = useTiptapEditor(providedEditor);

  const headingInSchema = isNodeInSchema("heading", editor);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const getActiveIcon = React.useCallback(() => {
    if (!editor)
      return (
        <FontAwesomeIcon icon={faHeading} className="tiptap-button-icon" />
      );

    const activeLevel = levels.find((level) =>
      editor.isActive("heading", { level }),
    ) as Level | undefined;

    if (!activeLevel)
      return (
        <FontAwesomeIcon icon={faHeading} className="tiptap-button-icon" />
      );

    const activeIconDefinition = headingIcons[activeLevel];
    return (
      <FontAwesomeIcon
        icon={activeIconDefinition}
        className="tiptap-button-icon"
      />
    );
  }, [editor, levels]);

  const canToggleAnyHeading = React.useCallback((): boolean => {
    if (!editor) return false;
    return levels.some((level) =>
      editor.can().toggleNode("heading", "paragraph", { level }),
    );
  }, [editor, levels]);

  const isDisabled = !canToggleAnyHeading();
  const isAnyHeadingActive = editor?.isActive("heading") ?? false;

  const show = React.useMemo(() => {
    if (!headingInSchema) {
      return false;
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor?.state.selection)) {
        return false;
      }
    }

    return true;
  }, [headingInSchema, hideWhenUnavailable, editor]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <UnifiedButton
          type="button"
          disabled={isDisabled}
          data-style="ghost"
          data-active-state={isAnyHeadingActive ? "on" : "off"}
          data-disabled={isDisabled}
          role="button"
          tabIndex={-1}
          aria-label="Format text as heading"
          aria-pressed={isAnyHeadingActive}
          tooltip="Heading"
          {...props}
        >
          {getActiveIcon()}
          <FontAwesomeIcon
            icon={faChevronDown}
            className="tiptap-button-dropdown-small"
          />
        </UnifiedButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          {levels.map((level) => {
            const isActive = editor.isActive("heading", { level });
            const iconDefinition = headingIcons[level];
            return (
              <DropdownMenuItem
                key={`heading-${level}`}
                onClick={() => {
                  if (isActive) {
                    editor.chain().focus().setNode("paragraph").run();
                  } else {
                    editor
                      .chain()
                      .focus()
                      .toggleNode("heading", "paragraph", { level })
                      .run();
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={iconDefinition}
                  className="tiptap-button-icon mr-2"
                />
                <span className="tiptap-button-text">
                  {getFormattedHeadingName(level)}
                </span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
